import React from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line } from 'recharts';
import Icon from '../../../components/AppIcon';

const PatientSatisfactionAnalytics = ({ dateRange, provider, treatmentType }) => {
  const satisfactionDistribution = [
    { name: 'Very Satisfied', value: 312, percentage: 72 },
    { name: 'Satisfied', value: 89, percentage: 21 },
    { name: 'Neutral', value: 21, percentage: 5 },
    { name: 'Dissatisfied', value: 9, percentage: 2 }
  ];

  const COLORS = ['#059669', '#2563EB', '#D97706', '#DC2626'];

  const treatmentSatisfactionRatings = [
    { treatment: 'Preventive', rating: 4.9, responses: 156 },
    { treatment: 'Restorative', rating: 4.7, responses: 142 },
    { treatment: 'Endodontic', rating: 4.6, responses: 89 },
    { treatment: 'Periodontal', rating: 4.8, responses: 128 },
    { treatment: 'Prosthodontic', rating: 4.5, responses: 76 },
    { treatment: 'Oral Surgery', rating: 4.4, responses: 54 }
  ];

  const satisfactionTrends = [
    { month: 'Jul', nps: 78, satisfaction: 4.6 },
    { month: 'Aug', nps: 80, satisfaction: 4.7 },
    { month: 'Sep', nps: 82, satisfaction: 4.7 },
    { month: 'Oct', nps: 85, satisfaction: 4.8 },
    { month: 'Nov', nps: 84, satisfaction: 4.8 },
    { month: 'Dec', nps: 87, satisfaction: 4.9 },
    { month: 'Jan', nps: 89, satisfaction: 4.9 }
  ];

  const keyMetrics = [
    {
      label: 'Net Promoter Score',
      value: '89',
      change: '+7 pts',
      icon: 'TrendingUp',
      color: 'bg-success/10 text-success',
      subtitle: 'Industry benchmark: 72'
    },
    {
      label: 'Avg Satisfaction',
      value: '4.9',
      change: '+0.2',
      icon: 'Star',
      color: 'bg-warning/10 text-warning',
      subtitle: 'Out of 5.0'
    },
    {
      label: 'Response Rate',
      value: '82%',
      change: '+6%',
      icon: 'MessageSquare',
      color: 'bg-primary/10 text-primary',
      subtitle: 'Survey completion'
    },
    {
      label: 'Total Responses',
      value: '431',
      change: '+45',
      icon: 'Users',
      color: 'bg-muted text-muted-foreground',
      subtitle: 'This period'
    }
  ];

  const providerComparison = [
    { provider: 'Dr. Chen', nps: 92, satisfaction: 4.9, responses: 178 },
    { provider: 'Dr. Rodriguez', nps: 88, satisfaction: 4.9, responses: 165 },
    { provider: 'Dr. Thompson', nps: 85, satisfaction: 4.8, responses: 142 }
  ];

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
          <Icon name="Heart" size={20} className="text-primary" />
          Patient Satisfaction Analytics
        </h2>
        <span className="text-xs text-muted-foreground">
          {dateRange === 'day' ? 'Today' : dateRange === 'week' ? 'This Week' : dateRange === 'month' ? 'This Month' : dateRange === 'quarter' ? 'This Quarter' : 'This Year'}
        </span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {keyMetrics?.map((metric, index) => (
          <div key={index} className="bg-muted/50 rounded-lg p-3 border border-border">
            <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${metric?.color} mb-2`}>
              <Icon name={metric?.icon} size={16} />
            </div>
            <p className="text-2xl font-bold text-foreground mb-1">{metric?.value}</p>
            <p className="text-xs text-muted-foreground">{metric?.label}</p>
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs font-semibold text-success">{metric?.change}</span>
              <span className="text-xs text-muted-foreground">{metric?.subtitle}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-4">Satisfaction Distribution</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={satisfactionDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percentage }) => `${name}: ${percentage}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {satisfactionDistribution?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    color: 'var(--color-foreground)'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-foreground mb-4">Satisfaction Trends (7 Months)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={satisfactionTrends} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="month"
                  stroke="var(--color-muted-foreground)"
                  style={{ fontSize: '11px' }}
                />
                <YAxis
                  yAxisId="left"
                  stroke="var(--color-muted-foreground)"
                  style={{ fontSize: '11px' }}
                  domain={[70, 95]}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="var(--color-muted-foreground)"
                  style={{ fontSize: '11px' }}
                  domain={[4.0, 5.0]}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    color: 'var(--color-foreground)'
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="nps"
                  stroke="#2563EB"
                  strokeWidth={2}
                  name="NPS Score"
                  dot={{ fill: '#2563EB', r: 4 }}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="satisfaction"
                  stroke="#059669"
                  strokeWidth={2}
                  name="Avg Rating"
                  dot={{ fill: '#059669', r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-4">Treatment-Specific Satisfaction Ratings</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={treatmentSatisfactionRatings} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="treatment"
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '11px' }}
                angle={-15}
                textAnchor="end"
                height={60}
              />
              <YAxis
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '11px' }}
                domain={[0, 5]}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  color: 'var(--color-foreground)'
                }}
              />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Bar dataKey="rating" fill="#D97706" radius={[8, 8, 0, 0]} name="Avg Rating (out of 5)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-foreground mb-4">Provider Comparison</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {providerComparison?.map((prov, index) => (
            <div key={index} className="bg-muted/50 rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-foreground">{prov?.provider}</span>
                <div className="flex items-center gap-1">
                  <Icon name="Star" size={14} className="text-warning fill-warning" />
                  <span className="text-sm font-bold text-foreground">{prov?.satisfaction}</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">NPS Score</span>
                  <span className="text-sm font-semibold text-success">{prov?.nps}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Responses</span>
                  <span className="text-sm font-semibold text-foreground">{prov?.responses}</span>
                </div>
              </div>
              <div className="mt-3 w-full bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className="h-full bg-success transition-all duration-500"
                  style={{ width: `${(prov?.satisfaction / 5) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PatientSatisfactionAnalytics;